import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  Tooltip,
} from "@mui/material";
import AppsIcon from "@mui/icons-material/Apps";
import AssignmentIcon from "@mui/icons-material/Assignment";
import FunctionsIcon from "@mui/icons-material/Functions";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import ConstructionIcon from "@mui/icons-material/Construction";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DescriptionIcon from "@mui/icons-material/Description";
import ChangeHistoryIcon from "@mui/icons-material/ChangeHistory";
import {
  APP_MODULES,
  PLANT_MODULE_LICENCE_MAP,
} from "../../constants/appModules";
import useModuleAccess from "../../hooks/useModuleAccess";

const PLANT_MODULES = [
  { key: "task", label: "Action Items", path: "task", icon: AssignmentIcon },
  {
    key: "estimation",
    label: "Estimation",
    path: "estimation",
    icon: FunctionsIcon,
  },
  { key: "dcs", label: "EMS", path: "dcs", icon: DocumentScannerIcon },
  { key: "mcs", label: "PMS", path: "mcs", icon: ManageAccountsIcon },
  { key: "pcs", label: "CMS", path: "pcs", icon: ConstructionIcon },
  {
    key: "validation",
    label: "Validation",
    path: "validation",
    icon: ChangeHistoryIcon,
  },
  { key: "cms", label: "COS", path: "cms", icon: ChangeCircleIcon },
  { key: "pms", label: "PCS", path: "pms", icon: CalendarTodayIcon },
  { key: "mis", label: "MIS", path: "mis", icon: DescriptionIcon },
];

/**
 * Reusable shortcut for navigating between plant modules.
 * Use in plant table (pass plantId) or inside any plant module page (plantId from route).
 * @param {string} plantId - Plant ID
 * @param {boolean} compact - If true, renders as IconButton; otherwise as Button with label
 * @param {boolean} floating - If true, positions fixed top-right for use inside module pages
 * @param {object} sx - MUI sx prop for styling
 */
export default function PlantModuleNav({
  plantId,
  compact = false,
  floating = false,
  sx = {},
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const { hasModule } = useModuleAccess();

  const visibleModules = PLANT_MODULES.filter((module) =>
    hasModule(PLANT_MODULE_LICENCE_MAP[module.key] ?? APP_MODULES.EPCM),
  );

  const handleClick = (e) => {
    e?.stopPropagation?.();
    setAnchorEl(e?.currentTarget ?? e);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleModuleClick = (path) => {
    if (plantId) {
      navigate(`/plant/${plantId}/${path}`);
    }
    handleClose();
  };

  if (!plantId || visibleModules.length === 0) return null;

  const floatingSx = floating
    ? { position: "fixed", top: 80, right: 24, zIndex: 1000 }
    : {};

  return (
    <Box
      sx={{ ...floatingSx, ...sx }}
      component="span"
      className="no-export"
      onClick={(e) => e?.stopPropagation?.()}
    >
      <Tooltip title="Quick navigation to all modules">
        {compact ? (
          <IconButton
            color="primary"
            onClick={handleClick}
            size="small"
            aria-controls={open ? "plant-module-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
          >
            <AppsIcon />
          </IconButton>
        ) : (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AppsIcon />}
            onClick={handleClick}
            aria-controls={open ? "plant-module-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
          >
            Modules
          </Button>
        )}
      </Tooltip>
      <Menu
        id="plant-module-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "plant-module-button",
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {visibleModules.map(({ key, label, path, icon: Icon }) => (
          <MenuItem key={key} onClick={() => handleModuleClick(path)}>
            <ListItemIcon>
              <Icon color="primary" fontSize="small" />
            </ListItemIcon>
            <ListItemText>{label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
